import {
  ref, watch, computed, getCurrentInstance,
} from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'
import apiEndpoints from '@/constants/apiEndpoints'

export default function usePOReceivesWarehouseList() {
  const toast = useToast()
  const { proxy } = getCurrentInstance()

  // ========== Tables ==========
  const refPurchaseOrderReceiveWarehouseTable = ref(null)
  const refPurchaseOrderReceiveWarehouseTableOngoing = ref(null)
  const refPurchaseOrderReceiveWarehouseTableFinished = ref(null)

  const tableColumns = [
    { key: 'WarehouseReceiveNumber', label: 'Warehouse Receive Number', sortable: true },
    { key: 'PurchaseReceiveNumber', label: 'Purchase Receive Number', sortable: true },
    { key: 'TotalReceived', label: 'Total Received', sortable: true },
    { key: 'CreatedBy', label: 'Created By', sortable: true },
    { key: 'DateCreated', label: 'Date Created', sortable: true },
    { key: 'Action', sortable: false },
  ]

  // ========== Pagination & Sorting ==========
  const perPage = ref(10)
  const perPageOngoing = ref(10)
  const perPageFinished = ref(10)

  const totalPurchaseOrder = ref(0)
  const totalPurchaseOrderOngoing = ref(0)
  const totalPurchaseOrderFinished = ref(0)

  const currentPage = ref(1)
  const currentPageOngoing = ref(1)
  const currentPageFinished = ref(1)

  const perPageOptions = [10, 25, 50, 100]

  const searchQuery = ref('')
  const searchQueryOngoing = ref('')
  const searchQueryFinished = ref('')

  const sortBy = ref('id')
  const sortByOngoing = ref('id')
  const sortByFinished = ref('id')

  const isSortDirDesc = ref(true)

  const roleFilter = ref(null)
  const statusFilter = ref(null)

  // ========== Meta Info ==========
  const dataMeta = computed(() => {
    const localCount = refPurchaseOrderReceiveWarehouseTable.value
      ? refPurchaseOrderReceiveWarehouseTable.value.localItems.length
      : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localCount,
      of: totalPurchaseOrder.value,
    }
  })

  const dataMetaOngoing = computed(() => {
    const localCount = refPurchaseOrderReceiveWarehouseTableOngoing.value
      ? refPurchaseOrderReceiveWarehouseTableOngoing.value.localItems.length
      : 0
    return {
      from: perPageOngoing.value * (currentPageOngoing.value - 1) + (localCount ? 1 : 0),
      to: perPageOngoing.value * (currentPageOngoing.value - 1) + localCount,
      of: totalPurchaseOrderOngoing.value,
    }
  })

  const dataMetaFinished = computed(() => {
    const localCount = refPurchaseOrderReceiveWarehouseTableFinished.value
      ? refPurchaseOrderReceiveWarehouseTableFinished.value.localItems.length
      : 0
    return {
      from: perPageFinished.value * (currentPageFinished.value - 1) + (localCount ? 1 : 0),
      to: perPageFinished.value * (currentPageFinished.value - 1) + localCount,
      of: totalPurchaseOrderFinished.value,
    }
  })

  // ========== Refetch ==========
  const refetchData = () => {
    if (refPurchaseOrderReceiveWarehouseTable.value) refPurchaseOrderReceiveWarehouseTable.value.refresh()
    if (refPurchaseOrderReceiveWarehouseTableOngoing.value) refPurchaseOrderReceiveWarehouseTableOngoing.value.refresh()
    if (refPurchaseOrderReceiveWarehouseTableFinished.value) refPurchaseOrderReceiveWarehouseTableFinished.value.refresh()
  }

  // ========== Watchers ==========
  watch(
    [currentPage, perPage, searchQuery, roleFilter, statusFilter, sortBy, isSortDirDesc, currentPageOngoing, perPageOngoing, searchQueryOngoing, sortByOngoing, currentPageFinished, perPageFinished, searchQueryFinished, sortByFinished],
    () => refetchData(),
  )

  // ========== API Fetchers ==========
  const fetchPurchaseOrdersReceivesWarehouse = (ctx, callback) => {
    const offset = perPage.value * (currentPage.value - 1)
    const userToken = proxy.$cookies.get('userToken')

    axios
      .get(
        `${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_ORDER_RECEIVES_WAREHOUSE}?limit=${perPage.value}&offset=${offset}&search=${searchQuery.value}&is_finished=false`,
        { headers: { Authorization: `Bearer ${userToken}` } },
      )
      .then(res => {
        totalPurchaseOrder.value = res.data.data_count
        callback(res.data.data)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching Unconfirmed list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const fetchPurchaseOrdersReceivesWarehouseOngoing = (ctx, callback) => {
    const offset = perPageOngoing.value * (currentPageOngoing.value - 1)
    const userToken = proxy.$cookies.get('userToken')

    axios
      .get(
        `${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_ORDER_RECEIVES_WAREHOUSE_ONGOING}?limit=${perPageOngoing.value}&offset=${offset}&search=${searchQueryOngoing.value}&is_finished=false`,
        { headers: { Authorization: `Bearer ${userToken}` } },
      )
      .then(res => {
        totalPurchaseOrderOngoing.value = res.data.data_count
        callback(res.data.data)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching Ongoing list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const fetchPurchaseOrdersReceivesWarehouseFinished = (ctx, callback) => {
    const offset = perPageFinished.value * (currentPageFinished.value - 1)
    const userToken = proxy.$cookies.get('userToken')

    axios
      .get(
        `${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_ORDER_RECEIVES_WAREHOUSE}?limit=${perPage.value}&offset=${offset}&search=${searchQueryFinished.value}&is_finished=true`,
        { headers: { Authorization: `Bearer ${userToken}` } },
      )
      .then(res => {
        totalPurchaseOrderFinished.value = res.data.data_count
        callback(res.data.data)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching Finished list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // ========== UI Helpers ==========
  const resolvePurchaseOrderRoleVariant = role => {
    if (role === 'subscriber') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'maintainer') return 'success'
    if (role === 'editor') return 'info'
    if (role === 'super-admin') return 'danger'
    return 'primary'
  }

  const resolvePurchaseOrderRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'super-admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolvePurchaseOrderStatusVariant = status => {
    if (status === 'pending') return 'warning'
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'secondary'
    return 'primary'
  }

  // ========== Return ==========
  return {
    fetchPurchaseOrdersReceivesWarehouse,
    fetchPurchaseOrdersReceivesWarehouseOngoing,
    fetchPurchaseOrdersReceivesWarehouseFinished,
    tableColumns,
    perPage,
    perPageOngoing,
    perPageFinished,
    currentPage,
    currentPageOngoing,
    currentPageFinished,
    totalPurchaseOrder,
    totalPurchaseOrderOngoing,
    totalPurchaseOrderFinished,
    dataMeta,
    dataMetaOngoing,
    dataMetaFinished,
    perPageOptions,
    searchQuery,
    searchQueryOngoing,
    searchQueryFinished,
    sortBy,
    sortByOngoing,
    sortByFinished,
    isSortDirDesc,
    refPurchaseOrderReceiveWarehouseTable,
    refPurchaseOrderReceiveWarehouseTableOngoing,
    refPurchaseOrderReceiveWarehouseTableFinished,
    refetchData,
    resolvePurchaseOrderRoleVariant,
    resolvePurchaseOrderRoleIcon,
    resolvePurchaseOrderStatusVariant,
    roleFilter,
    statusFilter,
  }
}
